import { ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { UsersPagination } from '@/components/admin/UsersPagination';
import { UsersTable } from '@/components/admin/UsersTable';
import { Navbar } from '@/components/Navbar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  DEFAULT_ADMIN_USERS_PAGE_SIZE,
  useAdminUsers,
} from '@/hooks/useAdminUsers';
import { useDebounce } from '@/hooks/useDebounce';

const PAGE_SIZE = DEFAULT_ADMIN_USERS_PAGE_SIZE;

export default function AdminDashboardPage() {
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, isFetching, isError, error } = useAdminUsers({
    page,
    pageSize: PAGE_SIZE,
    searchValue: debouncedSearch,
  });

  const totalPages = useMemo(() => {
    if (!data) return 1;
    const pages = Math.ceil((data.total || 0) / PAGE_SIZE);
    return pages > 0 ? pages : 1;
  }, [data]);

  const handlePrevious = () => {
    setPage((current) => (current > 1 ? current - 1 : current));
  };

  const handleNext = () => {
    setPage((current) => (current < totalPages ? current + 1 : current));
  };

  const roleOptions = useMemo<string[]>(() => {
    const roles = new Set(['user', 'admin']);
    data?.users?.forEach((user) => {
      if (user.role) {
        roles.add(user.role);
      }
    });
    return Array.from(roles).sort((a, b) => a.localeCompare(b));
  }, [data?.users]);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-800">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 pt-28 pb-10 sm:px-6 lg:px-8">
        <Card className="border-neutral-200">
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2 text-neutral-600">
              <ShieldCheck className="h-5 w-5" aria-hidden />
              <span className="text-sm font-medium uppercase tracking-wider">
                Admin Tools
              </span>
            </div>
            <CardTitle className="text-2xl font-semibold text-neutral-900">
              User Management
            </CardTitle>
            <CardDescription className="text-neutral-600">
              Review all registered users, their roles, and account status.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm text-neutral-600">
                  {data?.total ? (
                    <>
                      Showing{' '}
                      <span className="font-medium text-neutral-900">
                        {Math.min(page * PAGE_SIZE, data.total)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium text-neutral-900">
                        {data.total}
                      </span>{' '}
                      users
                    </>
                  ) : (
                    'No users found yet.'
                  )}
                </p>
              </div>
              <div className="flex w-full items-center gap-3 lg:w-auto">
                <Input
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Search by email"
                  className="w-full lg:w-64"
                  aria-label="Search users by email"
                />
              </div>
            </div>

            <UsersTable
              users={data?.users}
              roleOptions={roleOptions}
              isLoading={isLoading}
              isFetching={isFetching}
              isError={isError}
              error={error}
            />

            {data && data.total > PAGE_SIZE ? (
              <UsersPagination
                page={page}
                totalPages={totalPages}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            ) : null}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
